import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileText, Save, Clock, ArrowLeft, Plus, X, Shield, Zap, Cloud, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const MAX_TABS = 10;

const NotepadPage = () => {
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabName, setEditingTabName] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(true);
  const saveTimeoutRef = useRef(null);
  const editInputRef = useRef(null);

  // Quill modules configuration for basic toolbar
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list',
    'link'
  ];

  // Load tabs from localStorage on component mount
  useEffect(() => {
    // Check if bookmark banner was previously dismissed
    const bookmarkDismissed = localStorage.getItem('notepad-bookmark-dismissed');
    if (bookmarkDismissed === 'true') {
      setShowBookmarkBanner(false);
    }

    const savedTabs = localStorage.getItem('notepad-tabs');
    const savedActiveTabId = localStorage.getItem('notepad-active-tab');
    const savedTime = localStorage.getItem('notepad-last-saved');
    
    if (savedTabs) {
      const parsedTabs = JSON.parse(savedTabs);
      setTabs(parsedTabs);
      
      if (savedActiveTabId && parsedTabs.find(tab => tab.id === savedActiveTabId)) {
        setActiveTabId(savedActiveTabId);
        const activeTab = parsedTabs.find(tab => tab.id === savedActiveTabId);
        if (activeTab) {
          updateCounts(activeTab.content);
        }
      } else if (parsedTabs.length > 0) {
        setActiveTabId(parsedTabs[0].id);
        updateCounts(parsedTabs[0].content);
      }
    } else {
      // Create first tab if none exists
      const firstTab = {
        id: Date.now().toString(),
        name: 'Note 1',
        content: ''
      };
      setTabs([firstTab]);
      setActiveTabId(firstTab.id);
      saveTabs([firstTab], firstTab.id);
    }
    
    if (savedTime) {
      setLastSaved(new Date(savedTime));
    }
  }, []);

  // Focus input when editing tab name
  useEffect(() => {
    if (editingTabId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTabId]);

  const saveTabs = (tabsToSave, activeId) => {
    localStorage.setItem('notepad-tabs', JSON.stringify(tabsToSave));
    if (activeId !== undefined) {
      localStorage.setItem('notepad-active-tab', activeId);
    }
    const now = new Date();
    localStorage.setItem('notepad-last-saved', now.toISOString());
    setLastSaved(now);
  };

  // Auto-save functionality
  useEffect(() => {
    if (tabs.length > 0 && activeTabId) {
      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for auto-save (500ms after user stops typing)
      saveTimeoutRef.current = setTimeout(() => {
        saveTabs(tabs, activeTabId);
      }, 500);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [tabs, activeTabId]);

  const updateCounts = (htmlContent) => {
    // Strip HTML tags to get plain text for counting
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    
    const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
    const chars = text.length;
    setWordCount(words);
    setCharCount(chars);
  };

  const handleContentChange = (value) => {
    const updatedTabs = tabs.map(tab => 
      tab.id === activeTabId ? { ...tab, content: value } : tab
    );
    setTabs(updatedTabs);
    updateCounts(value);
  };

  const addNewTab = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (tabs.length >= MAX_TABS) {
      alert(`Maximum ${MAX_TABS} tabs allowed`);
      return;
    }

    const newTab = {
      id: Date.now().toString(),
      name: `Note ${tabs.length + 1}`,
      content: ''
    };
    
    const updatedTabs = [...tabs, newTab];
    setTabs(updatedTabs);
    setActiveTabId(newTab.id);
    updateCounts('');
    saveTabs(updatedTabs, newTab.id);
  };

  const closeTab = (tabId, e) => {
    e.stopPropagation();
    
    if (tabs.length === 1) {
      alert('Cannot close the last tab');
      return;
    }

    const tabIndex = tabs.findIndex(tab => tab.id === tabId);
    const updatedTabs = tabs.filter(tab => tab.id !== tabId);
    setTabs(updatedTabs);

    // Switch to adjacent tab
    if (activeTabId === tabId) {
      const newActiveTab = updatedTabs[tabIndex] || updatedTabs[tabIndex - 1];
      setActiveTabId(newActiveTab.id);
      updateCounts(newActiveTab.content);
      saveTabs(updatedTabs, newActiveTab.id);
    } else {
      saveTabs(updatedTabs, activeTabId);
    }
  };

  const switchTab = (tabId) => {
    setActiveTabId(tabId);
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      updateCounts(tab.content);
      saveTabs(tabs, tabId);
    }
  };

  const startEditingTab = (tabId, currentName, e) => {
    e.stopPropagation();
    setEditingTabId(tabId);
    setEditingTabName(currentName);
  };

  const finishEditingTab = () => {
    if (editingTabId && editingTabName.trim()) {
      const updatedTabs = tabs.map(tab =>
        tab.id === editingTabId ? { ...tab, name: editingTabName.trim() } : tab
      );
      setTabs(updatedTabs);
      saveTabs(updatedTabs, activeTabId);
    }
    setEditingTabId(null);
    setEditingTabName('');
  };

  const handleTabNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      finishEditingTab();
    } else if (e.key === 'Escape') {
      setEditingTabId(null);
      setEditingTabName('');
    }
  };

  const getActiveTabContent = () => {
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    return activeTab ? activeTab.content : '';
  };

  const formatLastSaved = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return date.toLocaleDateString();
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleDismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('notepad-bookmark-dismissed', 'true');
  };

  const faqs = [
    {
      question: "Is this online notepad free to use?",
      answer: "Yes! Our online notepad is completely free to use. You can write, save, and edit unlimited notes without any registration or login. All your notes are securely saved in your browser's local storage."
    },
    {
      question: "How secure is my data in this web-based notepad?",
      answer: "Your data is very secure. All notes are saved locally in your browser's storage and never sent to our servers. This means only you have access to your notes, and they remain private on your device."
    },
    {
      question: "Can I use this online text editor on mobile devices?",
      answer: "Absolutely! Our online notepad is fully responsive and works perfectly on smartphones, tablets, and desktop computers. You can access your notes from any device with a web browser."
    },
    {
      question: "Will my notes be saved if I close the browser?",
      answer: "Yes! Your notes are automatically saved to your browser's local storage. When you return to the online notepad, all your notes and tabs will be exactly as you left them, even after closing the browser or restarting your device."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Online Notepad — Simple, Free Text Editor in Your Browser | AllSmallTooLS</title>
        <meta name="description" content="Write, edit, and save notes instantly in your browser. A clean, distraction-free online notepad — no login or installation required." />
        <meta name="keywords" content="online notepad, text editor, free notepad, simple notes online, browser notepad" />
        <link rel="canonical" href="https://allsmalltools.com/online-notepad" />
      </Helmet>

      <div className="space-y-6">
        {/* Bookmark Banner */}
        {showBookmarkBanner && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-indigo-200 rounded-2xl p-4 sm:p-6 shadow-lg">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3 text-center sm:text-left">
                <div className="bg-indigo-100 p-3 rounded-full flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </div>
                <div>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    Bookmark this page for quick access to notepad anytime
                  </p>
                  <p className="text-sm text-gray-600 hidden sm:block">
                    Press Ctrl+D (Windows/Linux) or Cmd+D (Mac) to bookmark
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    // Try to trigger bookmark dialog directly
                    const isMac = navigator.userAgent.toLowerCase().indexOf('mac') !== -1;
                    const shortcut = isMac ? 'Cmd+D' : 'Ctrl+D';
                    
                    // Attempt legacy methods first
                    if (window.sidebar && window.sidebar.addPanel) {
                      // Firefox
                      window.sidebar.addPanel(document.title, window.location.href, '');
                    } else if (window.external && ('AddFavorite' in window.external)) {
                      // Internet Explorer
                      window.external.AddFavorite(window.location.href, document.title);
                    } else {
                      // Modern browsers - show friendly message
                      const message = `To bookmark this page, press ${shortcut} on your keyboard.\n\nThis will save quick access to your notepad for future use.`;
                      alert(message);
                    }
                  }}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 flex items-center space-x-2 whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                  <span>Bookmark Now</span>
                </button>

                {/* Close Button - Prominent and on the right */}
                <button
                  onClick={handleDismissBookmarkBanner}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 hover:text-gray-900 p-3 rounded-lg transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
                  aria-label="Dismiss bookmark banner"
                  title="Don't show again"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-2">
                <FileText className="h-8 w-8 text-blue-500" />
                <span>Online Notepad</span>
              </h1>
              <p className="text-gray-600">Your free online text editor - automatically saves your notes</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">{wordCount}</div>
              <div className="text-xs text-gray-500">Words</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">{charCount}</div>
              <div className="text-xs text-gray-500">Characters</div>
            </div>
          </div>
        </div>

        {/* Tabs Bar and Notepad - No spacing between them */}
        <div>
          {/* Tabs Bar */}
          <div className="bg-white rounded-t-lg border border-b-0 border-gray-200 shadow-sm">
            <div className="flex items-center px-2 py-1 overflow-x-auto">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  onClick={() => switchTab(tab.id)}
                  className={`
                    group flex items-center gap-2 px-4 py-2 mr-1 rounded-t-lg cursor-pointer
                    transition-all duration-200 min-w-[120px] max-w-[200px]
                    ${activeTabId === tab.id 
                      ? 'bg-blue-50 border-t-2 border-x border-blue-500 text-blue-700' 
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-600 border border-transparent'
                    }
                  `}
                >
                  {editingTabId === tab.id ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingTabName}
                      onChange={(e) => setEditingTabName(e.target.value)}
                      onBlur={finishEditingTab}
                      onKeyDown={handleTabNameKeyDown}
                      className="flex-1 px-1 py-0 text-sm bg-white border border-blue-500 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span
                      onDoubleClick={(e) => startEditingTab(tab.id, tab.name, e)}
                      className="flex-1 text-sm font-medium truncate"
                      title="Double-click to rename"
                    >
                      {tab.name}
                    </span>
                  )}
                  
                  {tabs.length > 1 && (
                    <button
                      onClick={(e) => closeTab(tab.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-red-100 rounded"
                      title="Close tab"
                    >
                      <X className="h-3.5 w-3.5 text-gray-500 hover:text-red-600" />
                    </button>
                  )}
                </div>
              ))}
              
              {tabs.length < MAX_TABS && (
                <button
                  onClick={addNewTab}
                  className="flex items-center justify-center w-8 h-8 ml-1 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                  title={`Add new tab (${tabs.length}/${MAX_TABS})`}
                >
                  <Plus className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Notepad */}
          <Card className="h-[calc(100vh-320px)] rounded-t-none">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Save className="h-5 w-5" />
                  <span>{tabs.find(t => t.id === activeTabId)?.name || 'My Notes'}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>Last saved: {formatLastSaved(lastSaved)}</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="h-[calc(100%-80px)]">
              <ReactQuill
                key={activeTabId}
                theme="snow"
                value={getActiveTabContent()}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                placeholder="Start typing your notes here... Your content will be automatically saved."
                className="h-full"
                style={{
                  height: 'calc(100% - 42px)'
                }}
              />
            </CardContent>
          </Card>
        </div>

        {/* Footer Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Badge className="bg-blue-100 text-blue-800">
                  Auto-Save Enabled
                </Badge>
                <div className="text-sm text-blue-700">
                  Your notes are automatically saved to your browser's local storage
                </div>
              </div>
              <div className="text-xs text-blue-600">
                {tabs.length}/{MAX_TABS} tabs • Double-click tab to rename
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why Use Our Online Notepad Section */}
        <Card className="mt-12">
          <CardHeader>
            <h2 className="text-2xl font-bold text-gray-900">Why Use Our Online Notepad?</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-start space-y-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Fast & Simple</h3>
                <p className="text-gray-600">
                  Start writing instantly with our online text editor. No downloads, no installations, no registration required. Just open and type – it's that simple!
                </p>
              </div>
              
              <div className="flex flex-col items-start space-y-3">
                <div className="bg-green-100 p-3 rounded-lg">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">100% Secure</h3>
                <p className="text-gray-600">
                  Your notes stay private on your device. This web-based notepad stores everything locally in your browser – we never see or access your data.
                </p>
              </div>
              
              <div className="flex flex-col items-start space-y-3">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Cloud className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Auto-Save</h3>
                <p className="text-gray-600">
                  Never lose your work! Our online notepad automatically saves every change. Close your browser and come back anytime – your notes will be waiting.
                </p>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700">
                Whether you need a quick note-taking app for work, a simple notepad for ideas, or a reliable online text editor for drafting – we've got you covered. Access your notes from any device, anytime. Try our free online notepad today and experience the easiest way to take notes online!
              </p>
              <div className="mt-4 flex flex-wrap gap-4">
                <Link to="/case-converter" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Case Converter Tool
                </Link>
                <Link to="/split" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → Split PDF Tool
                </Link>
                <Link to="/json-beautifier" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  → JSON Beautifier
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Save Your Notes Securely Section */}
        <Card>
          <CardHeader>
            <h3 className="text-xl font-bold text-gray-900">How to Save Your Notes Securely</h3>
          </CardHeader>
          <CardContent>
            <ol className="space-y-4 text-gray-700">
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">1</span>
                <div>
                  <strong>Start Writing:</strong> Simply type your notes in the online notepad editor above. Use the formatting toolbar for bold, italic, lists, and more.
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">2</span>
                <div>
                  <strong>Automatic Saving:</strong> Your notes are automatically saved every few seconds. No need to click save – the online text editor does it for you!
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">3</span>
                <div>
                  <strong>Organize with Tabs:</strong> Use multiple tabs to organize different notes. Double-click any tab name to rename it for better organization.
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-semibold">4</span>
                <div>
                  <strong>Access Anytime:</strong> Your notes persist in your browser. Come back to this web-based notepad anytime to continue where you left off.
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card>
          <CardHeader>
            <h2 className="text-2xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                    {openFaq === index ? (
                      <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="p-4 bg-white">
                      <p className="text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default NotepadPage;
